import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, TrendingUp, Shield, Globe, Battery, Wind, Sun } from 'lucide-react';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';

const FloatingIcon = ({ icon: Icon, delay = 0, position = "top" }: { icon: any, delay?: number, position?: string }) => {
    const positions = {
        top: "top-20",
        bottom: "bottom-20",
        left: "left-10",
        right: "right-10"
    };

    return (
        <motion.div
            className={`absolute ${positions[position as keyof typeof positions]} w-12 h-12 glass-card rounded-full flex items-center justify-center`}
            animate={{
                y: [0, -20, 0],
                rotate: [0, 5, -5, 0],
            }}
            transition={{
                duration: 4,
                delay,
                repeat: Infinity,
                ease: "easeInOut"
            }}
            style={{
                background: 'rgba(110, 255, 157, 0.1)',
                border: '1px solid rgba(110, 255, 157, 0.3)',
                boxShadow: '0 0 20px rgba(110, 255, 157, 0.2)'
            }}
        >
            <Icon className="w-6 h-6" style={{ color: 'var(--neon-green)' }} />
        </motion.div>
    );
};

const EnergyConnection = ({ from, to }: { from: string, to: string }) => {
    return (
        <motion.div
            className="absolute"
            style={{
                left: from.includes('left') ? '20%' : '80%',
                top: from.includes('top') ? '30%' : '70%',
                width: '200px',
                height: '2px',
                background: 'linear-gradient(90deg, var(--neon-green), var(--cyber-blue), transparent)',
                transform: 'rotate(45deg)',
                opacity: 0.6
            }}
            animate={{
                opacity: [0.3, 0.8, 0.3],
                scaleX: [0.5, 1, 0.5]
            }}
            transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
            }}
        />
    );
};

const FuturisticHero: React.FC = () => {
    const { user } = useFirebaseAuth();

    return (
        <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--primary-bg)' }}>
            {/* Animated Background Grid */}
            <div className="absolute inset-0 opacity-20">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `
              linear-gradient(rgba(110, 255, 157, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(110, 255, 157, 0.1) 1px, transparent 1px)
            `,
                        backgroundSize: '50px 50px'
                    }}
                />
            </div>

            {/* Floating Energy Icons */}
            <FloatingIcon icon={Sun} delay={0} position="top" />
            <FloatingIcon icon={Wind} delay={1} position="right" />
            <FloatingIcon icon={Battery} delay={2} position="bottom" />
            <FloatingIcon icon={Globe} delay={0.5} position="left" />

            {/* Energy Connection Lines */}
            <EnergyConnection from="top-left" to="bottom-right" />
            <EnergyConnection from="top-right" to="bottom-left" />

            {/* Main Content */}
            <div className="relative z-10 container mx-auto px-4 py-20">
                {/* Header */}
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <motion.div
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full mb-8"
                        style={{
                            background: 'rgba(110, 255, 157, 0.1)',
                            border: '1px solid rgba(110, 255, 157, 0.3)',
                            backdropFilter: 'blur(10px)'
                        }}
                        whileHover={{ scale: 1.05 }}
                    >
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--neon-green)' }}></div>
                        <span className="text-sm font-mono font-bold" style={{ color: 'var(--neon-green)' }}>
                            BLOCKCHAIN-POWERED RENEWABLE ENERGY
                        </span>
                    </motion.div>

                    <motion.h1
                        className="text-5xl md:text-7xl font-bold font-mono mb-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                    >
                        <span className="block holo-text">INVEST IN</span>
                        <span className="block" style={{
                            background: 'linear-gradient(135deg, var(--neon-green), var(--cyber-blue))',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '0 0 30px rgba(110, 255, 157, 0.5)'
                        }}>
                            GREEN ENERGY
                        </span>
                        <span className="block holo-text">FUTURE</span>
                    </motion.h1>

                    <motion.p
                        className="text-xl md:text-2xl max-w-4xl mx-auto mb-8 font-light"
                        style={{ color: 'var(--secondary-text)' }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.8 }}
                    >
                        Join the blockchain revolution in renewable energy. Invest in solar farms, wind turbines,
                        and battery storage systems with transparent, secure, and profitable returns.
                    </motion.p>

                    <motion.div
                        className="flex flex-col sm:flex-row gap-6 justify-center items-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.8 }}
                    >
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            {user ? (
                                <Link
                                    to="/projects"
                                    className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300"
                                    style={{
                                        background: 'linear-gradient(135deg, var(--neon-green), var(--eco-green))',
                                        color: 'var(--primary-bg)',
                                        boxShadow: '0 0 30px rgba(110, 255, 157, 0.4)'
                                    }}
                                >
                                    <Zap className="w-6 h-6" />
                                    START INVESTING
                                </Link>
                            ) : (
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300"
                                    style={{
                                        background: 'linear-gradient(135deg, var(--neon-green), var(--eco-green))',
                                        color: 'var(--primary-bg)',
                                        boxShadow: '0 0 30px rgba(110, 255, 157, 0.4)'
                                    }}
                                >
                                    <Zap className="w-6 h-6" />
                                    START INVESTING
                                </Link>
                            )}
                        </motion.div>

                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Link
                                to="/how-it-works"
                                className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300"
                                style={{
                                    background: 'rgba(14, 165, 233, 0.1)',
                                    border: '2px solid var(--cyber-blue)',
                                    color: 'var(--cyber-blue)',
                                    backdropFilter: 'blur(10px)'
                                }}
                            >
                                <TrendingUp className="w-6 h-6" />
                                LEARN MORE
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>

                {/* 3D Energy Systems Visualization */}
                <motion.div
                    className="relative max-w-6xl mx-auto mb-16"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8, duration: 1 }}
                >
                    <div className="glass-card p-8 rounded-3xl relative overflow-hidden">
                        {/* Central Energy Hub */}
                        <div className="text-center mb-12">
                            <motion.div
                                className="w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center"
                                style={{
                                    background: 'radial-gradient(circle, rgba(110, 255, 157, 0.2), rgba(14, 165, 233, 0.1))',
                                    border: '3px solid var(--neon-green)',
                                    boxShadow: '0 0 50px rgba(110, 255, 157, 0.3)'
                                }}
                                animate={{
                                    rotate: 360,
                                    scale: [1, 1.1, 1]
                                }}
                                transition={{
                                    rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                                    scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                                }}
                            >
                                <div className="text-4xl font-bold font-mono" style={{ color: 'var(--neon-green)' }}>8</div>
                            </motion.div>
                            <h2 className="text-3xl font-bold font-mono mb-2" style={{ color: 'var(--neon-green)' }}>
                                GREENOVA8
                            </h2>
                            <p className="text-sm" style={{ color: 'var(--secondary-text)' }}>
                                Blockchain Energy Network
                            </p>
                        </div>

                        {/* Energy Systems Grid */}
                        <div className="grid md:grid-cols-3 gap-8">
                            {/* Solar Farm */}
                            <motion.div
                                className="text-center"
                                whileHover={{ y: -10, scale: 1.05 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="glass-card p-6 rounded-2xl mb-4 relative overflow-hidden">
                                    <img
                                        src="/assets/visuals/solar-panel-array.svg"
                                        alt="Solar Farm"
                                        className="w-full h-32 object-cover rounded-lg mb-4 opacity-90"
                                    />
                                    <div className="absolute top-2 right-2 w-3 h-3 rounded-full animate-pulse" style={{ background: 'var(--neon-green)' }}></div>
                                    <h3 className="text-lg font-bold font-mono mb-2" style={{ color: 'var(--neon-green)' }}>
                                        SOLAR FARMS
                                    </h3>
                                    <div className="text-2xl font-bold text-white mb-1">2.4 MW</div>
                                    <div className="text-xs" style={{ color: 'var(--secondary-text)' }}>ACTIVE CAPACITY</div>
                                </div>
                            </motion.div>

                            {/* Wind Turbines */}
                            <motion.div
                                className="text-center"
                                whileHover={{ y: -10, scale: 1.05 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="glass-card p-6 rounded-2xl mb-4 relative overflow-hidden">
                                    <img
                                        src="/assets/visuals/wind-turbine-farm.svg"
                                        alt="Wind Farm"
                                        className="w-full h-32 object-cover rounded-lg mb-4 opacity-90"
                                    />
                                    <div className="absolute top-2 right-2 w-3 h-3 rounded-full animate-pulse" style={{ background: 'var(--cyber-blue)' }}></div>
                                    <h3 className="text-lg font-bold font-mono mb-2" style={{ color: 'var(--cyber-blue)' }}>
                                        WIND TURBINES
                                    </h3>
                                    <div className="text-2xl font-bold text-white mb-1">1.8 MW</div>
                                    <div className="text-xs" style={{ color: 'var(--secondary-text)' }}>ACTIVE CAPACITY</div>
                                </div>
                            </motion.div>

                            {/* Battery Storage */}
                            <motion.div
                                className="text-center"
                                whileHover={{ y: -10, scale: 1.05 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="glass-card p-6 rounded-2xl mb-4 relative overflow-hidden">
                                    <img
                                        src="/assets/visuals/battery-storage-system.svg"
                                        alt="Battery Storage"
                                        className="w-full h-32 object-cover rounded-lg mb-4 opacity-90"
                                    />
                                    <div className="absolute top-2 right-2 w-3 h-3 rounded-full animate-pulse" style={{ background: 'var(--electric-purple)' }}></div>
                                    <h3 className="text-lg font-bold font-mono mb-2" style={{ color: 'var(--electric-purple)' }}>
                                        BATTERY STORAGE
                                    </h3>
                                    <div className="text-2xl font-bold text-white mb-1">500 kWh</div>
                                    <div className="text-xs" style={{ color: 'var(--secondary-text)' }}>STORAGE CAPACITY</div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Performance Metrics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                            <div className="glass-card p-4 rounded-xl text-center">
                                <div className="text-xs font-mono mb-1" style={{ color: 'var(--neon-green)' }}>TOTAL AUM</div>
                                <div className="text-xl font-bold text-white">$2.5M</div>
                                <div className="text-xs" style={{ color: 'var(--secondary-text)' }}>+12.5%</div>
                            </div>
                            <div className="glass-card p-4 rounded-xl text-center">
                                <div className="text-xs font-mono mb-1" style={{ color: 'var(--cyber-blue)' }}>AVG ROI</div>
                                <div className="text-xl font-bold text-white">26.2%</div>
                                <div className="text-xs" style={{ color: 'var(--secondary-text)' }}>YTD</div>
                            </div>
                            <div className="glass-card p-4 rounded-xl text-center">
                                <div className="text-xs font-mono mb-1" style={{ color: 'var(--electric-purple)' }}>INVESTORS</div>
                                <div className="text-xl font-bold text-white">1,247</div>
                                <div className="text-xs" style={{ color: 'var(--secondary-text)' }}>ACTIVE</div>
                            </div>
                            <div className="glass-card p-4 rounded-xl text-center">
                                <div className="text-xs font-mono mb-1" style={{ color: 'var(--warning)' }}>PROJECTS</div>
                                <div className="text-xl font-bold text-white">25+</div>
                                <div className="text-xs" style={{ color: 'var(--secondary-text)' }}>LIVE</div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Trust Indicators */}
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.8 }}
                >
                    <div className="flex flex-wrap justify-center items-center gap-8 mb-8">
                        <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5" style={{ color: 'var(--neon-green)' }} />
                            <span className="text-sm font-mono" style={{ color: 'var(--secondary-text)' }}>
                                BLOCKCHAIN SECURED
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Globe className="w-5 h-5" style={{ color: 'var(--cyber-blue)' }} />
                            <span className="text-sm font-mono" style={{ color: 'var(--secondary-text)' }}>
                                GLOBAL IMPACT
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Zap className="w-5 h-5" style={{ color: 'var(--electric-purple)' }} />
                            <span className="text-sm font-mono" style={{ color: 'var(--secondary-text)' }}>
                                REAL-TIME TRACKING
                            </span>
                        </div>
                    </div>

                    <p className="text-sm" style={{ color: 'var(--dim-text)' }}>
                        *Past performance doesn't guarantee future returns. All investments carry risk.
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default FuturisticHero;

